import React, { useState } from 'react';
import { Form, Button, Spinner } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { showToast as toast } from '../toastManager';
import axios from 'axios';
import { applyLoginPayloadToCookies } from '../authCookies';
import { useBrand } from "../theme/brand";

const BLOCKED_EMAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com', 'yahoo.com', 'yahoo.co.uk', 'yahoo.co.in', 'ymail.com',
  'hotmail.com', 'hotmail.co.uk', 'outlook.com', 'live.com', 'msn.com',
  'icloud.com', 'me.com', 'mac.com', 'aol.com', 'protonmail.com', 'proton.me',
  'mail.com', 'gmx.com', 'gmx.net', 'yandex.com', 'yandex.ru',
  'hey.com', 'fastmail.com', 'tutanota.com', 'pm.me', 'duck.com', 'qq.com', '163.com'
]);

function isBusinessEmail(email) {
  const norm = String(email || '').trim().toLowerCase();
  const at = norm.lastIndexOf('@');
  if (at < 1) return { ok: false, message: 'Enter a valid business email address.' };
  const domain = norm.slice(at + 1);
  if (BLOCKED_EMAIL_DOMAINS.has(domain)) {
    return {
      ok: false,
      message: 'Use your work email. Gmail, Yahoo, Hotmail, and similar personal addresses are not allowed.'
    };
  }
  return { ok: true };
}

function Signup({ app_url }) {
  const brand = useBrand();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const googleInitialized = React.useRef(false);
  const googleBtnRef = React.useRef(null);

  const [step, setStep] = useState(1);
  const [verifiedEmail, setVerifiedEmail] = useState('');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!email || email.trim() === '') newErrors.email = 'Email is required.';
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = 'Please enter a valid email address.';
    else {
      const biz = isBusinessEmail(email);
      if (!biz.ok) newErrors.email = biz.message;
    }

    if (!password || password.trim() === '') newErrors.password = 'Password is required.';
    else if (password.trim().length < 6) newErrors.password = 'Password must be at least 6 characters.';


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateVerify = () => {
    const newErrors = {};
    if (!verificationCode || `${verificationCode}`.trim() === '') {
      newErrors.code = 'Enter the 6-digit code from your email.';
    } else if (`${verificationCode}`.replace(/\D/g, '').length !== 6) {
      newErrors.code = 'Code must be 6 digits.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const resp = await axios.post(
        `${app_url}/signup`,
        { email: email.trim(), password: password.trim() },
        { withCredentials: true }
      );

      if (resp.data?.success && resp.data?.data?.requires_verification) {
        setVerifiedEmail(resp.data.data.email || email.trim().toLowerCase());
        setStep(2);
        toast.success(resp.data?.message || 'Check your email for the verification code.');
      } else if (resp.data?.success) {
        toast.success(resp.data?.message || 'Signup successful.');
        navigate('/login', { replace: true, state: { email: email.trim() } });
      } else {
        toast.error(resp.data?.message || 'Signup failed');
      }
    } catch (err) {
      const data = err?.response?.data;
      if (err?.response?.status === 409 && data?.data?.requires_verification && data?.data?.email) {
        setVerifiedEmail(data.data.email);
        setStep(2);
        toast.error(data?.message || 'Finish verifying this email.');
        return;
      }
      toast.error(data?.message || err?.message || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!validateVerify()) return;

    setVerifyLoading(true);
    try {
      const resp = await axios.post(
        `${app_url}/signup/verify`,
        { email: verifiedEmail, code: String(verificationCode).trim() },
        { withCredentials: true }
      );

      if (resp.data?.success) {
        toast.success(resp.data?.message || 'Email verified.');
        applyLoginPayloadToCookies(resp.data, navigate);
      } else {
        toast.error(resp.data?.message || 'Verification failed');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Verification failed');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    try {
      const resp = await axios.post(
        `${app_url}/signup/resend`,
        { email: verifiedEmail, password: password.trim() },
        { withCredentials: true }
      );
      if (resp.data?.success) {
        toast.success(resp.data?.message || 'A new code was sent.');
      } else {
        toast.error(resp.data?.message || 'Could not resend');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not resend');
    } finally {
      setResendLoading(false);
    }
  };

  const loadGoogleScript = () => {
    if (window.google?.accounts?.id) return Promise.resolve(true);
    const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    if (existing) {
      return new Promise((resolve, reject) => {
        existing.addEventListener('load', () => resolve(true), { once: true });
        existing.addEventListener('error', () => reject(new Error('Failed to load Google script')), { once: true });
      });
    }
    return new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://accounts.google.com/gsi/client';
      s.async = true;
      s.defer = true;
      s.onload = () => resolve(true);
      s.onerror = () => reject(new Error('Failed to load Google script'));
      document.body.appendChild(s);
    });
  };

  const handleGoogleSignup = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    // if (googleLoading) return;
    setGoogleLoading(true);
    try {
      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
      if (!clientId) {
        toast.error('Google Sign-In is not configured (missing REACT_APP_GOOGLE_CLIENT_ID).');
        setGoogleLoading(false);
        return;
      }
      await loadGoogleScript();

      // Initialize once — use renderButton (popup-based) instead of prompt() to avoid FedCM issues
      if (!googleInitialized.current) {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: async (resp) => {
            try {
              const r = await axios.post(`${app_url}/login/google`, { credential: resp.credential }, { withCredentials: true });
              if (r.data?.success) {
                applyLoginPayloadToCookies(r.data, navigate);
              } else {
                toast.error(r.data?.message || 'Google sign-up failed');
              }
            } catch (err) {
              toast.error(err?.response?.data?.message || 'Google sign-up failed');
            } finally {
              setGoogleLoading(false);
            }
          },
        });
        if (googleBtnRef.current) {
          window.google.accounts.id.renderButton(googleBtnRef.current, {
            type: 'standard',
            theme: 'outline',
            size: 'large',
          });
        }
        googleInitialized.current = true;
      }

      // Trigger the rendered Google button popup (works for all accounts, no FedCM)
      const innerBtn = googleBtnRef.current?.querySelector('div[role="button"]');
      if (innerBtn) {
        innerBtn.click();
      } else {
        toast.error('Google sign-up could not be initialized. Please refresh and try again.');
        setGoogleLoading(false);
      }
    } catch (err) {
      toast.error(err?.message || 'Google sign-up failed');
      setGoogleLoading(false);
    }
  };

  return (
    <>
      <Toaster />
      <div style={{ display: 'flex', minHeight: '100vh' }}>
        {/* Left Panel */}
        <div style={{
          flex: '0 0 50%',
          backgroundColor: 'var(--cux-btn, #132848)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img width="52" height="52" src={brand.logo} alt={`${brand.name} logo`} style={{ objectFit: 'contain' }} />
            <span style={{ fontSize: '2rem', fontWeight: '700', color: 'white', letterSpacing: '-0.5px' }}>{brand.name}</span>
          </div>
        </div>

        {/* Right Panel */}
        <div style={{
          flex: '0 0 50%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px',
          backgroundColor: '#fff',
          overflowY: 'auto',
        }}>
          <div style={{ width: '100%', maxWidth: '400px' }}>
            {step === 1 ? (
              <>
                <h1 style={{ fontSize: '1.75rem', fontWeight: '700', color: '#101828', marginBottom: '8px', marginTop: 0 }}>
                  Create an account
                </h1>
                <p style={{ fontSize: '14px', color: '#667085', marginBottom: '28px', marginTop: 0 }}>
                  Use your business email. We will send a 6-digit code to verify it.
                </p>

                {/* Sign up with Google */}
                <Button variant="outline-secondary" type="button" className="w-100 mb-4" onClick={handleGoogleSignup}
                  style={{ backgroundColor: 'white', color: '#344054', borderColor: '#D0D5DD', padding: '10px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', boxShadow: '0 1px 2px rgba(16,24,40,0.05)', opacity: 1 }}>
                  {/* {googleLoading
                    ? <Spinner size="sm" style={{ borderColor: '#344054', borderRightColor: 'transparent' }} />
                    : <img width="20" height="20" src="https://developers.google.com/identity/images/g-logo.png" alt="" />
                  }
                  {googleLoading ? 'Signing up...' : 'Sign up with Google'} */}
                  <img width="20" height="20" src="https://developers.google.com/identity/images/g-logo.png" alt="" />
                  Sign up with Google
                </Button>

                {/* Divider */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                  <div style={{ flex: 1, height: '1px', backgroundColor: '#EAECF0' }} />
                  <span style={{ fontSize: '14px', color: '#667085', whiteSpace: 'nowrap' }}>or</span>
                  <div style={{ flex: 1, height: '1px', backgroundColor: '#EAECF0' }} />
                </div>

                <Form onSubmit={handleSignup} noValidate>
                  <Form.Group className="mb-4" controlId="signupEmail">
                    <Form.Label style={{ fontSize: '14px', fontWeight: '500', color: '#344054', marginBottom: '6px', display: 'block' }}>Business email</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value.trim())}
                      isInvalid={!!errors.email}
                      style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #D0D5DD', fontSize: '14px', boxShadow: '0 1px 2px rgba(16,24,40,0.05)' }}
                    />
                    <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-4" controlId="signupPassword">
                    <Form.Label style={{ fontSize: '14px', fontWeight: '500', color: '#344054', marginBottom: '6px', display: 'block' }}>Password</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Create a password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      isInvalid={!!errors.password}
                      style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #D0D5DD', fontSize: '14px', boxShadow: '0 1px 2px rgba(16,24,40,0.05)' }}
                    />
                    <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
                  </Form.Group>


                  <Button variant="primary" type="submit" disabled={loading} className="w-100 mb-3"
                    style={{ backgroundColor: 'var(--cux-btn, #132848)', borderColor: 'var(--cux-btn, #132848)', padding: '10px', borderRadius: '8px', fontSize: '1rem', fontWeight: '600' }}>
                    {loading ? <Spinner size="sm" /> : 'Create account'}
                  </Button>

                  <div style={{ textAlign: 'center', fontSize: '14px', color: '#667085' }}>
                    Already have an account?{' '}
                    <Link to="/login" style={{ color: 'var(--cux-ink, #132848)', textDecoration: 'none', fontWeight: '600' }}>Log in</Link>
                  </div>
                </Form>
              </>
            ) : (
              <>
                <h1 style={{ fontSize: '1.75rem', fontWeight: '700', color: '#101828', marginBottom: '8px', marginTop: 0 }}>
                  Verify your email
                </h1>
                <p style={{ fontSize: '14px', color: '#667085', marginBottom: '28px', marginTop: 0 }}>
                  Enter the 6-digit code sent to <strong>{verifiedEmail}</strong>. The same code is stored on your account and works with the sign-in passcode step after you verify.
                </p>

                <Form onSubmit={handleVerify} noValidate>
                  <Form.Group className="mb-4" controlId="signupVerificationCode">
                    <Form.Label style={{ fontSize: '14px', fontWeight: '500', color: '#344054', marginBottom: '6px', display: 'block' }}>Verification code</Form.Label>
                    <Form.Control
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="000000"
                      maxLength={6}
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      isInvalid={!!errors.code}
                      style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #D0D5DD', fontSize: '18px', letterSpacing: '0.2em', boxShadow: '0 1px 2px rgba(16,24,40,0.05)' }}
                    />
                    <Form.Control.Feedback type="invalid">{errors.code}</Form.Control.Feedback>
                  </Form.Group>

                  <Button variant="primary" type="submit" disabled={verifyLoading} className="w-100 mb-3"
                    style={{ backgroundColor: 'var(--cux-btn, #132848)', borderColor: 'var(--cux-btn, #132848)', padding: '10px', borderRadius: '8px', fontSize: '1rem', fontWeight: '600' }}>
                    {verifyLoading ? <Spinner size="sm" /> : 'Verify email'}
                  </Button>

                  <Button variant="outline-secondary" type="button" className="w-100 mb-3" disabled={resendLoading} onClick={handleResend}
                    style={{ backgroundColor: 'white', color: '#344054', borderColor: '#D0D5DD', padding: '10px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', boxShadow: '0 1px 2px rgba(16,24,40,0.05)' }}>
                    {resendLoading ? <Spinner size="sm" /> : 'Resend code'}
                  </Button>

                  <button type="button" className="btn btn-link p-0 text-decoration-none"
                    style={{ fontSize: '14px', color: 'var(--cux-ink, #132848)', fontWeight: '600' }}
                    onClick={() => { setStep(1); setVerificationCode(''); }}>
                    ← Back
                  </button>
                </Form>
              </>
            )}
          </div>
        </div>
      </div>
      {/* Hidden container for Google's rendered button — needed for popup-based sign-in */}
      <div ref={googleBtnRef} style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', zIndex: -1 }} aria-hidden="true" />
    </>
  );
}

export default Signup;
